import { useTableDetailsResource } from 'hooks/useApi';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { RunningItem } from 'olo-service';
import { CLIENT_PATH } from 'olo-service/axiosInstance';
import { useEffect, useMemo } from 'react';
import { fromCents } from 'util/Formatting';
import { generateId } from 'util/generateId';
import { LocalStorage } from 'util/Storage';
import { useMerchant } from './merchant';
import { useServiceType } from './serviceType';
import { TableDetails } from 'olo-service/models';

export const useRunningOrder = () => {
  const { merchant } = useMerchant();
  const { typeMetadata } = useServiceType();
  const navigate = useSmartNavigate();

  const merchantId = merchant?.merchantUuid;

  const table = useTableDetailsResource(merchantId, typeMetadata.tableUuid);

  // If there exists an order id in local storage, but not on the table, someone else has paid for the order; redirect to status
  useEffect(() => {
    if (table.isIdle || table.isLoading) return;

    const tableItem = LocalStorage.tryGetItem(`colo-table-details_${merchantId}`);
    const localTableInfo = tableItem ? JSON.parse(tableItem) as TableDetails : undefined;
    const orderId = table.data?.order?.id;
    if (orderId !== undefined || orderId === localTableInfo?.order?.id) return;

    if (localTableInfo?.order?.id) {
      navigate(`/${CLIENT_PATH}/${merchant?.slug}/status`, {
        replace: true,
        state: {
          receiptId: localTableInfo?.order?.id,
        },
      });
      LocalStorage.tryRemoveItem(`colo-table-details_${merchantId}`);
    }

  }, [merchant?.slug, merchantId, navigate, table]);

  const items = useMemo<RunningItem[]>(() => {
    const tableItems = table.data?.order?.cart?.items;
    if (!tableItems) return [];

    return tableItems.map(item => {
      return {
        ...item,
        modifiers: item.modifiers,
        quantity: item.quantity,
        total: fromCents(item.price),
        subtotal: item.price,
        cartId: generateId()
      };
    });
  }, [table.data?.order?.cart?.items]);

  return {
    ...table,
    items,
    note: useMemo(() => table.data?.order?.note || '', [table.data?.order?.note]),
    total: useMemo(
      () => (table.data?.order?.total ? fromCents(table.data?.order?.total) : ''),
      [table.data?.order?.total]
    ),
    hasItems: !!items?.length,
  };
};
